import React, { Component, Suspense } from "react";
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import { useLocation } from "react-router-dom";

export function PageLoading() {
  return (
    <Box role="status" aria-live="polite" sx={{ p: 4, minHeight: 180, display: "grid", placeContent: "center", justifyItems: "center", gap: 2 }}>
      <CircularProgress size={28} />
      <Typography color="text.secondary">Loading page…</Typography>
    </Box>
  );
}

class PageLoadErrorBoundary extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    console.error("Page could not load", error);
  }

  componentDidUpdate(previousProps) {
    if (this.state.error && previousProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <Box sx={{ p: 3, maxWidth: 560, mx: "auto" }}>
          <Alert severity="error">This page could not load. Check your connection and try again.</Alert>
          <Button variant="contained" sx={{ mt: 2 }} onClick={() => window.location.reload()}>
            Reload page
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

// Reloading fetches the current app version when a deployment removes an old chunk.
// Reset only errors on navigation; keep the dashboard shell mounted normally.
export default function PageLoadBoundary({ children, fallback = <PageLoading /> }) {
  const { pathname } = useLocation();
  return (
    <PageLoadErrorBoundary resetKey={pathname}>
      <Suspense fallback={fallback}>{children}</Suspense>
    </PageLoadErrorBoundary>
  );
}
